import { useState } from "react"
import { Plus } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  addStop,
  checkNewStop,
  currentAddress,
  knownCodes,
  sameAddressStops,
  setAddress,
  suggestedAddress,
  type DailyRoutesDraft,
  type StopRef,
} from "@/lib/dailyRoutes/draft"
import { norm } from "@/lib/dailyRoutes/names"
import { SameAddressDialog } from "./SameAddressDialog"
import { useTranslation } from "react-i18next"
import type { TFunction } from "i18next"

type AddStopFormProps = {
  route: string
  draft: DailyRoutesDraft
  disabled: boolean
  onChange: (next: DailyRoutesDraft) => void
}

const MAX_SUGGESTIONS = 10

const suggestionsFor = (draft: DailyRoutesDraft, input: string): string[] => {
  const wanted = norm(input)
  if (!wanted) return []
  return knownCodes(draft)
    .filter((code) => code !== input.trim() && norm(code).includes(wanted))
    .slice(0, MAX_SUGGESTIONS)
}

export const problem = (
  t: TFunction,
  check: ReturnType<typeof checkNewStop>,
  code: string,
) => {
  if (check.ok) return null
  if (check.reason === "empty") return t("maps.addStopForm.enterCode")
  if (check.reason === "on-route")
    return t("maps.addStopForm.alreadyOnRoute", { code: code.trim() })
  return t("maps.addStopForm.looksTheSame", { existing: check.existing })
}

export function AddStopForm({
  route,
  draft,
  disabled,
  onChange,
}: AddStopFormProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [code, setCode] = useState("")
  // null until the user types, so the existing address shows through
  const [typedAddress, setTypedAddress] = useState<string | null>(null)
  const [touched, setTouched] = useState(false)
  // an edited address of an existing code that other codes share, waiting
  // for the user to say whether those follow
  const [question, setQuestion] = useState<{
    code: string
    address: string
    others: StopRef[]
  } | null>(null)

  const close = () => {
    setOpen(false)
    setCode("")
    setTypedAddress(null)
    setTouched(false)
    setQuestion(null)
  }

  if (!open) {
    return (
      <Button
        type="button"
        variant="ghost"
        size="sm"
        disabled={disabled}
        onClick={() => setOpen(true)}
      >
        {t("maps.addStopForm.addStopButton")}
      </Button>
    )
  }

  const check = checkNewStop(draft, route, code)
  const existing = check.ok ? check.existing : null
  // the code's address on this route; one from another route only prefills
  const own =
    existing === null ? undefined : currentAddress(draft, route, existing)
  const known =
    existing === null ? undefined : suggestedAddress(draft, route, existing)
  const address = typedAddress ?? known ?? ""
  const message = touched ? problem(t, check, code) : null
  const canAdd = check.ok && address.trim() !== "" && !disabled

  // the typed address belongs to the code it was typed for: it survives
  // edits of a new code and goes when the form moves to another known code
  const changeCode = (next: string) => {
    const nextCheck = checkNewStop(draft, route, next)
    const nextExisting = nextCheck.ok ? nextCheck.existing : null
    if (nextExisting !== existing) setTypedAddress(null)
    setCode(next)
    setTouched(true)
  }

  const submit = () => {
    setTouched(true)
    if (!canAdd) return
    const changed =
      existing !== null && own !== undefined && address.trim() === own.trim()
        ? null
        : address
    if (existing !== null && own !== undefined && changed !== null) {
      const others = sameAddressStops(draft, route, existing)
      if (others.length > 0) {
        setQuestion({ code: existing, address: changed, others })
        return
      }
    }
    onChange(addStop(draft, route, code, changed))
    close()
  }

  const answer = (all: boolean) => {
    if (!question) return
    const { code: added, address: edited, others } = question
    const withStop = addStop(draft, route, added, edited)
    onChange(
      all
        ? setAddress(withStop, [{ route, code: added }, ...others], edited)
        : withStop,
    )
    close()
  }

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault()
      submit()
    } else if (event.key === "Escape") {
      close()
    }
  }

  return (
    <div className="flex flex-col gap-2 rounded-md border p-3">
      <div className="flex items-center gap-2">
        <Plus className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        <Input
          aria-label={t("maps.addStopForm.stopCode")}
          placeholder={t("maps.addStopForm.code")}
          autoFocus
          disabled={disabled}
          value={code}
          onChange={(event) => changeCode(event.target.value)}
          onKeyDown={onKeyDown}
        />
      </div>
      <p className="text-xs text-muted-foreground">
        {t("maps.addStopForm.codeHint")}
      </p>
      {suggestionsFor(draft, code).length > 0 && (
        <ul className="flex flex-wrap gap-1">
          {suggestionsFor(draft, code).map((suggestion) => (
            <li key={suggestion}>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={disabled}
                onClick={() => changeCode(suggestion)}
              >
                {suggestion}
              </Button>
            </li>
          ))}
        </ul>
      )}
      {message && (
        <p role="alert" className="text-xs text-destructive">
          {message}
        </p>
      )}
      <Input
        aria-label={t("maps.addStopForm.address")}
        placeholder={t("maps.addStopForm.address")}
        disabled={disabled}
        value={address}
        onChange={(event) => setTypedAddress(event.target.value)}
        onKeyDown={onKeyDown}
      />
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={close}>
          {t("common.cancel")}
        </Button>
        <Button type="button" size="sm" disabled={!canAdd} onClick={submit}>
          {t("common.add")}
        </Button>
      </div>
      {question && (
        <SameAddressDialog
          route={route}
          code={question.code}
          others={question.others}
          onChangeAll={() => answer(true)}
          onOnlyThis={() => answer(false)}
          onCancel={() => setQuestion(null)}
        />
      )}
    </div>
  )
}
